import type { MetricCardData } from '../components/AnalyticsCard';
import type { PieChartData } from '../components/PieChartCard';
import type { BarChartData } from '../components/BarChartCard';
import { AnalyticsMetric, MetricCardType } from '../../../const';
import { AnalyticsMetricLabel } from '../../../../conversations/const';

export const metricCardTemplates: MetricCardData[] = [
  {
    id: '1',
    title: 'Average Conversation Time',
    value: '47',
    unit: 'min',
    type: MetricCardType.DURATION_SECONDS,
  },
  {
    id: '2',
    title: 'Average AI Response Time',
    value: '1.2',
    unit: 'sec',
    type: MetricCardType.AVERAGE_RESPONSE_TIME_SECONDS,
  },
  {
    id: '3',
    title: 'Customer Satisfaction (CSAT) Score',
    value: '4.7',
    unit: 'stars',
    type: MetricCardType.CSAT_STARS,
  },
];

export const pieChartTemplates: PieChartData[] = [
  {
    id: 'pie-1',
    title: AnalyticsMetricLabel.SUCCESS_RATE,
    type: AnalyticsMetric.SUCCESS_RATE,
    unit: '%',
    data: [
      { name: 'Successful Conversations', value: 87, color: '#06E17E' },
      { name: 'Not Successful Conversations', value: 13, color: '#DDE5E8' },
    ],
  },
  {
    id: 'pie-2',
    title: AnalyticsMetricLabel.ERROR_RATE,
    type: AnalyticsMetric.ERROR_RATE,
    unit: '%',
    data: [
      { name: 'Conversations with Error', value: 5, color: '#F14E42' },
      { name: 'Conversations without Error', value: 95, color: '#DDE5E8' },
    ],
  },
  {
    id: 'pie-3',
    title: AnalyticsMetricLabel.USER_ABANDONED,
    type: AnalyticsMetric.USER_ABANDONED,
    unit: '%',
    data: [
      { name: 'Conversations Left', value: 12, color: '#0E88E9' },
      { name: 'Conversations Completed', value: 88, color: '#DDE5E8' },
    ],
  },
  {
    id: 'pie-4',
    title: AnalyticsMetricLabel.SYSTEM_AVAILABILITY,
    type: AnalyticsMetric.SYSTEM_AVAILABILITY,
    data: [
      { name: 'Available', value: 99.9, color: '#10b981' },
      { name: 'Downtime', value: 0.1, color: '#DDE5E8' },
    ],
  },
  {
    id: 'pie-5',
    title: AnalyticsMetricLabel.TOPIC_TAGS,
    type: AnalyticsMetric.TOPIC_TAGS,
    data: [
      {
        name: 'Customer Support Question',
        value: 35.2,
        originalValue: 352,
        percentage: 35.2,
        color: '#8B5CF6',
      },
      {
        name: 'Technical Issue',
        value: 24.8,
        originalValue: 248,
        percentage: 24.8,
        color: '#06E17E',
      },
      {
        name: 'Billing Inquiry',
        value: 18.5,
        originalValue: 185,
        percentage: 18.5,
        color: '#F14E42',
      },
      {
        name: 'Product Information',
        value: 12.3,
        originalValue: 123,
        percentage: 12.3,
        color: '#0E88E9',
      },
      {
        name: 'Account Management',
        value: 6.7,
        originalValue: 67,
        percentage: 6.7,
        color: '#10b981',
      },
      {
        name: 'Feature Request',
        value: 2.5,
        originalValue: 25,
        percentage: 2.5,
        color: '#F59E0B',
      },
    ],
    totalSum: 1000,
  },
  {
    id: 'pie-6',
    title: AnalyticsMetricLabel.MOOD_TAGS,
    type: AnalyticsMetric.MOOD_TAGS,
    data: [],
    totalSum: 0,
  },
];

export const chartTemplates: BarChartData[] = [
  {
    id: 'chart-1',
    title: 'Number of Escalations to Human',
    type: AnalyticsMetric.HUMAN_ESCALATION,
    data: [
      { name: '00:00', value: 2 },
      { name: '04:00', value: 1 },
      { name: '08:00', value: 3 },
      { name: '12:00', value: 4 },
      { name: '16:00', value: 5 },
      { name: '20:00', value: 2 },
    ],
    periodData: {
      TODAY: [
        { name: '00:00', value: 2 },
        { name: '04:00', value: 1 },
        { name: '08:00', value: 3 },
        { name: '12:00', value: 4 },
        { name: '16:00', value: 5 },
        { name: '20:00', value: 2 },
      ],
      WEEK: [
        { name: 'Mon', value: 12 },
        { name: 'Tue', value: 8 },
        { name: 'Wed', value: 15 },
        { name: 'Thu', value: 10 },
        { name: 'Fri', value: 18 },
        { name: 'Sat', value: 6 },
        { name: 'Sun', value: 9 },
      ],
      MONTH: [
        { name: 'Week 1', value: 45 },
        { name: 'Week 2', value: 52 },
        { name: 'Week 3', value: 38 },
        { name: 'Week 4', value: 41 },
      ],
      YEAR: [
        { name: 'Jan', value: 180 },
        { name: 'Feb', value: 165 },
        { name: 'Mar', value: 195 },
        { name: 'Apr', value: 172 },
        { name: 'May', value: 210 },
        { name: 'Jun', value: 188 },
        { name: 'Jul', value: 225 },
        { name: 'Aug', value: 198 },
        { name: 'Sep', value: 185 },
        { name: 'Oct', value: 220 },
        { name: 'Nov', value: 175 },
        { name: 'Dec', value: 190 },
      ],
    },
  },
  {
    id: 'chart-2',
    title: 'Max Concurrency',
    type: AnalyticsMetric.MAX_CONCURRENCY,
    data: [
      { name: '00:00', value: 25 },
      { name: '04:00', value: 18 },
      { name: '08:00', value: 45 },
      { name: '12:00', value: 62 },
      { name: '16:00', value: 58 },
      { name: '20:00', value: 35 },
    ],
    periodData: {
      TODAY: [
        { name: '00:00', value: 25 },
        { name: '04:00', value: 18 },
        { name: '08:00', value: 45 },
        { name: '12:00', value: 62 },
        { name: '16:00', value: 58 },
        { name: '20:00', value: 35 },
      ],
      WEEK: [
        { name: 'Mon', value: 55 },
        { name: 'Tue', value: 62 },
        { name: 'Wed', value: 48 },
        { name: 'Thu', value: 65 },
        { name: 'Fri', value: 58 },
        { name: 'Sat', value: 35 },
        { name: 'Sun', value: 42 },
      ],
      MONTH: [
        { name: 'Week 1', value: 58 },
        { name: 'Week 2', value: 62 },
        { name: 'Week 3', value: 55 },
        { name: 'Week 4', value: 60 },
      ],
      YEAR: [
        { name: 'Jan', value: 55 },
        { name: 'Feb', value: 58 },
        { name: 'Mar', value: 62 },
        { name: 'Apr', value: 57 },
        { name: 'May', value: 65 },
        { name: 'Jun', value: 60 },
        { name: 'Jul', value: 63 },
        { name: 'Aug', value: 59 },
        { name: 'Sep', value: 61 },
        { name: 'Oct', value: 64 },
        { name: 'Nov', value: 56 },
        { name: 'Dec', value: 58 },
      ],
    },
  },
];

export const getCardTemplate = (type: string, id: string) => {
  const allTemplates = [
    ...metricCardTemplates,
    ...pieChartTemplates,
    ...chartTemplates,
  ];
  return allTemplates.find(template => template.id === id);
};

export const updateCardData = (template: any, apiData: any) => {
  return {
    ...template,
    ...apiData,
  };
};
